import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import Icon from '@mdi/react';
import { mdiTshirtCrew } from '@mdi/js';

function EditTeamModal({ show, handleClose, team, onTeamUpdated }) {
    const [formData, setFormData] = useState({
        name: '',
        logo: null,
        color: '',
        captain: '',
        a_cap: ''
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [colorChoices, setColorChoices] = useState([]);
    const [players, setPlayers] = useState([]);

    useEffect(() => {
        if (team) {
            // roster comes directly from team.players
            setPlayers(team.players || []);

            setFormData({
                name: team.name || '',
                logo: null,
                color: team.color || '',
                captain: team.captain || '',
                a_cap: team.a_cap || ''
            });
        }
    }, [team]);

    useEffect(() => {
        // Fetch color choices when component mounts
        const fetchColorChoices = async () => {
            try {
                const response = await axios.get('/api/teams/team-color-choices/');
                setColorChoices(response.data.choices);
            } catch (err) {
                console.error('Failed to fetch color choices:', err);
            }
        };
        fetchColorChoices();
    }, []);

    const handleChange = (e) => {
        const { name, value, files } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: files ? files[0] : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const formDataToSend = new FormData();
            formDataToSend.append('name', formData.name);
            formDataToSend.append('color', formData.color);
            if (formData.logo) {
                formDataToSend.append('logo', formData.logo);
            }
            if (!team.edit_lock) {
                if (formData.captain) {
                    formDataToSend.append('captain', formData.captain);
                }
                if (formData.a_cap) {
                    formDataToSend.append('a_cap', formData.a_cap);
                }
            }

            const response = await axios.patch(
                `/api/teams/${team.id}/`,
                formDataToSend,
                {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                    withCredentials: true,
                }
            );

            onTeamUpdated(response.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to update team');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Edit Team</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleSubmit}>
                <Modal.Body>
                    {error && <div className="alert alert-danger">{error}</div>}

                    <Row className="mb-3">
                        <Col>
                            <Form.Group controlId="name">
                                <Form.Label>Team Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                    disabled={team?.edit_lock}
                                />
                            </Form.Group>
                        </Col>
                    </Row>

                    {!team?.edit_lock && (
                        <Row className="mb-3">
                            <Col md={6}>
                                <Form.Group controlId="captain">
                                    <Form.Label>Captain</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="captain"
                                        value={formData.captain}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option key="captain-empty" value="">Select a captain</option>
                                        {players.map((player) => (
                                            <option key={`captain-${player.id}`} value={player.id}>
                                                {player.first_name} {player.last_name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group controlId="a_cap">
                                    <Form.Label>Assistant Captain</Form.Label>
                                    <Form.Control
                                        as="select"
                                        name="a_cap"
                                        value={formData.a_cap}
                                        onChange={handleChange}
                                    >
                                        <option key="a_cap-empty" value="">Select an assistant captain</option>
                                        {players.map((player) => (
                                            <option key={`a_cap-${player.id}`} value={player.id}>
                                                {player.first_name} {player.last_name}
                                            </option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                        </Row>
                    )}

                    <Row className="mb-3">
                        <Col md={6}>
                            <Form.Group controlId="color">
                                <Form.Label>Team Color</Form.Label>
                                <Form.Select
                                    name="color"
                                    value={formData.color}
                                    onChange={handleChange}
                                    required
                                    disabled={team?.edit_lock}
                                >
                                    <option value="">Select a color</option>
                                    {colorChoices.map(([value, label]) => (
                                        <option key={value} value={value}>
                                            {label}
                                        </option>
                                    ))}
                                </Form.Select>
                                <div className="text-center mt-2">
                                    <Icon
                                        path={mdiTshirtCrew}
                                        size={11}
                                        style={{ color: formData.color || "black" }}
                                    />
                                </div>
                            </Form.Group>
                        </Col>
                        <Col md={6}>
                            <Form.Group controlId="logo">
                                <Form.Label>Team Logo</Form.Label>
                                <Form.Control
                                    type="file"
                                    name="logo"
                                    onChange={handleChange}
                                    accept="image/*"
                                />
                                {team?.logo && (
                                    <div className="mt-2">
                                        <small>Current logo:</small>
                                        <img
                                            src={team.logo}
                                            alt="Current team logo"
                                            style={{ maxWidth: '100px', maxHeight: '100px' }}
                                            className="ms-2"
                                        />
                                    </div>
                                )}
                            </Form.Group>
                        </Col>
                    </Row>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button variant="primary" type="submit" disabled={loading}>
                        {loading ? 'Saving...' : 'Save Changes'}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}

export default EditTeamModal; 